/**
 * Delivery-indicator state, kept separate from the component that draws it so
 * the mapping is testable as pure logic (importing the component drags in
 * React, icons, and the whole types graph).
 */

/**
 * `simulated` and `blocked` are the Outbound Gate's answers (ENG-3373): the text was built and
 * recorded but never sent — the org's stream is off or the recipient is not allowed — or the
 * person had opted out. Neither may read as a tick.
 */
export type DeliveryTickState = "sent" | "delivered" | "seen" | "undelivered" | "failed" | "simulated" | "blocked";

/**
 * Map a stored `deliveryStatus` to a tick state for an OUTBOUND (assistant or
 * agent) message, from the perspective of someone watching the conversation.
 *
 * `undelivered` and `failed` exist because voice produces outcomes a checkmark
 * cannot honestly represent: a barge-in means the caller heard only the stored
 * prefix, and a synthesis failure means they heard nothing at all. Flattening
 * either into "sent" reads as success.
 *
 * Returns null when there is no delivery data, so a surface shows nothing
 * rather than implying a state it doesn't know.
 */
export function outboundTickState(deliveryStatus?: string, seenByUser?: boolean): DeliveryTickState | null {
  if (seenByUser) return "seen";
  switch (deliveryStatus) {
    case undefined:
    case "":
      return null;
    case "delivered":
      return "delivered";
    case "undelivered":
      return "undelivered";
    case "failed":
      return "failed";
    case "simulated":
    case "sandboxed":
      return "simulated";
    case "blocked":
    case "opted_out":
      return "blocked";
    default:
      // sending / queued / sent — in flight, or not a delivery outcome we distinguish visually.
      return "sent";
  }
}
